import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { RecurringEvent } from "./RecurringEventService";
import { User } from "./UserService";
import { Room } from "./RoomService";

// GraphQL queries
const RECURRING_EVENT_TEAM_LIST_QUERY = gql`
  query RecurringEventTeamList(
    $filter: RecurringEventTeamFilter
    $params: PaginationAndSorting
  ) {
    recurringEventTeamList(filter: $filter, params: $params) {
      code
      data {
        id
        recurringEventId
        teamName
        teamType
        contactPerson
        phone
        email
        notes
        orderNo
        createdAt
        createdBy
        supportingRooms {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RECURRING_EVENT_TEAM_GET_QUERY = gql`
  query RecurringEventTeamGet($id: ID) {
    recurringEventTeamGet(id: $id) {
      code
      data {
        id
        recurringEventId
        teamName
        teamType
        contactPerson
        phone
        email
        notes
        orderNo
        createdAt
        createdBy
        supportingRooms {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RECURRING_EVENT_TEAM_GET_MANY_QUERY = gql`
  query RecurringEventTeamGetMany($ids: [ID]) {
    recurringEventTeamGetMany(ids: $ids) {
      code
      data {
        id
        recurringEventId
        teamName
        teamType
        contactPerson
        phone
        email
        notes
        orderNo
        createdAt
        createdBy
        supportingRooms {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const RECURRING_EVENT_TEAM_CREATE_MUTATION = gql`
  mutation RecurringEventTeamCreate($params: RecurringEventTeamInput) {
    recurringEventTeamCreate(params: $params) {
      code
      data {
        id
        recurringEventId
        teamName
        teamType
        contactPerson
        phone
        email
        notes
        orderNo
        createdAt
        createdBy
        supportingRooms {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RECURRING_EVENT_TEAM_UPDATE_MUTATION = gql`
  mutation RecurringEventTeamUpdate(
    $where: RecurringEventTeamWhereInput
    $params: RecurringEventTeamInput
  ) {
    recurringEventTeamUpdate(where: $where, params: $params) {
      code
      data {
        id
        recurringEventId
        teamName
        teamType
        contactPerson
        phone
        email
        notes
        orderNo
        createdAt
        createdBy
        supportingRooms {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RECURRING_EVENT_TEAM_REMOVE_MUTATION = gql`
  mutation RecurringEventTeamRemove($where: RecurringEventTeamWhereInput) {
    recurringEventTeamRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const RECURRING_EVENT_TEAM_REMOVE_MANY_MUTATION = gql`
  mutation RecurringEventTeamRemoveMany(
    $where: [RecurringEventTeamWhereInput]
  ) {
    recurringEventTeamRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface RecurringEventTeam {
  id: string;
  recurringEventId?: string;
  teamName?: string;
  teamType?: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  notes?: string;
  orderNo?: string;
  createdAt?: string;
  createdBy?: string;
  userCreated?: User;
  recurringEvent?: RecurringEvent;
  supportingRooms?: Room[];
}

export interface RecurringEventTeamFilter {
  id?: string[];
  recurringEventId?: string[];
  teamName?: string;
  teamType?: string[];
  contactPerson?: string;
  email?: string;
}

export interface RecurringEventTeamInput {
  recurringEventId?: string | null | undefined;
  teamName?: string | null | undefined;
  teamType?: string | null | undefined;
  contactPerson?: string | null | undefined;
  phone?: string | null | undefined;
  email?: string | null | undefined;
  notes?: string | null | undefined;
  orderNo?: string | null | undefined;
  supportingRooms?: string[] | null | undefined;
}

export interface RecurringEventTeamWhereInput {
  id: string;
}

/**
 * Recurring Event Team service that handles all recurring event team-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class RecurringEventTeamService {
  /**
   * Get a list of recurring event teams with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: RecurringEventTeamFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      RECURRING_EVENT_TEAM_LIST_QUERY,
      {
        filter: filter || {},
        params: {
          pagination: pagination || undefined,
          sortBy: sortBy || undefined,
        },
      }
    );
    return result;
  }

  /**
   * Get a single recurring event team by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      RECURRING_EVENT_TEAM_GET_QUERY,
      {
        id: id,
      }
    );
    return result;
  }

  /**
   * Get multiple recurring event teams by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      RECURRING_EVENT_TEAM_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }

  /**
   * Create a new recurring event team
   */
  static async create(params: RecurringEventTeamInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      RECURRING_EVENT_TEAM_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing recurring event team
   */
  static async update(
    id: string,
    params: RecurringEventTeamInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      RECURRING_EVENT_TEAM_UPDATE_MUTATION,
      {
        where: { id },
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a recurring event team
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      RECURRING_EVENT_TEAM_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple recurring event teams
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      RECURRING_EVENT_TEAM_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
